import {useCallback, useMemo, useState} from 'react';
import {t} from '../common/getTranslations';
import {stopPropagationEvent} from '../common/shared/utils/stopPropagationEvent.function';
import useTypedDispatch from '../common/useTypedDispatch';
import useTypedSelector from '../common/useTypedSelector';
import {ButtonV2Variant} from '../design-system/atoms/ButtonV2/ButtonV2.enum';
import {Dialog} from '../design-system/organisms/Dialog';
import {useDialog} from '../dialog/useDialog.hook';
import {setIsIpxMediaNetworkUsingAgreed} from './configSlice';

interface UseNetworkWarningDialogProps {
  onClickOk: () => void;
  onClickCancel?: () => void;
  onClose?: () => void;
  isMobile: boolean;
}

export function useNetworkUsingAgreement({
  onClickOk: _onClickOk,
  onClickCancel: _onClickCancel,
  onClose: _onClose,
  isMobile,
}: UseNetworkWarningDialogProps) {
  const [isNeverSeeAgainChecked, setIsNeverSeeAgainChecked] = useState(false);
  const onClose = useCallback(() => {
    setIsNeverSeeAgainChecked(false);
    _onClose?.();
  }, [_onClose]);
  const {DialogWrapper, showDialog, hideDialog} = useDialog({
    isMobile,
    onClose,
  });

  const dispatch = useTypedDispatch();
  const isNetworkUsingAgreed = useTypedSelector(
    state => state.config.isIpxMediaNetworkUsingAgreed
  );

  const onToggleChecked = useCallback(() => {
    setIsNeverSeeAgainChecked(prev => !prev);
  }, []);

  const onClickOk = useCallback(() => {
    if (isNeverSeeAgainChecked) {
      dispatch(setIsIpxMediaNetworkUsingAgreed(true));
    }
    _onClickOk();
    hideDialog();
  }, [_onClickOk, dispatch, hideDialog, isNeverSeeAgainChecked]);

  const onClickCancel = useCallback(() => {
    hideDialog();
    _onClickCancel?.();
  }, [_onClickCancel, hideDialog]);

  const DialogComponent = useMemo(() => {
    return (
      <DialogWrapper
        onClick={stopPropagationEvent}
        onTouchStart={stopPropagationEvent}
        onTouchEnd={stopPropagationEvent}
        onTouchMove={stopPropagationEvent}
        onMouseDown={stopPropagationEvent}
        onMouseUp={stopPropagationEvent}
      >
        <Dialog.Dimmed />
        <Dialog.Title>{t('alert_message_cellular_title')}</Dialog.Title>
        <Dialog.List>
          <Dialog.Label>{t('alert_message_cellular_description')}</Dialog.Label>
          <Dialog.Check
            onChange={onToggleChecked}
            isChecked={isNeverSeeAgainChecked}
          >
            {t('common_do_not_show_again')}
          </Dialog.Check>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClickCancel}>
            {t('common_cancel_button')}
          </Dialog.Button>
          <Dialog.Button onClick={onClickOk} variant={ButtonV2Variant.Primary}>
            {t('common_confirm_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    );
  }, [
    DialogWrapper,
    onClickCancel,
    onToggleChecked,
    isNeverSeeAgainChecked,
    onClickOk,
  ]);

  return {
    // PC는 현재 설정과 상관 없이 항상 데이터 사용동의한 것으로 간주됩니다.
    isNetworkUsingAgreed: isMobile ? isNetworkUsingAgreed : true,
    showAgreementDialog: showDialog,
    hideAgreementDialog: hideDialog,
    DialogComponent,
  };
}
